export const LMS_QUESTIONS_FORMAT = 'lms-questions' as const;
export const LMS_QUESTIONS_SCHEMA_VERSION = 1 as const;

export const QUESTION_TYPES = [
  'single_choice',
  'multiple_choice',
  'true_false',
  'short_answer',
  'long_answer',
  'file_upload',
] as const;

export type QuestionType = (typeof QUESTION_TYPES)[number];

/** Respuesta larga y subir archivo no se autocorrigen. */
export function questionTypeRequiresReview(type: string): boolean {
  return type === 'long_answer' || type === 'file_upload';
}

export const TITLE_MAX_LENGTH = 1000;
export const STEM_MAX_LENGTH = 200_000;
export const OPTION_CONTENT_MAX_LENGTH = 20_000;

const ENVELOPE_KEYS = new Set(['format', 'schemaVersion', 'questions', 'skipped']);
const QUESTION_KEYS = new Set([
  'title',
  'type',
  'stem',
  'explanation',
  'feedbackCorrect',
  'feedbackIncorrect',
  'options',
  'requiresReview',
]);
const OPTION_KEYS = new Set(['label', 'content', 'isCorrect']);

export interface LmsQuestionOption {
  label?: string;
  content: string;
  isCorrect: boolean;
}

export interface LmsQuestion {
  title: string;
  type: QuestionType;
  stem: string;
  explanation: string | null;
  feedbackCorrect: string | null;
  feedbackIncorrect: string | null;
  requiresReview: boolean;
  options?: LmsQuestionOption[];
}

export interface LmsQuestionsSkipped {
  reason: string;
  sourceExcerpt?: string;
}

export interface LmsQuestionsFile {
  format: typeof LMS_QUESTIONS_FORMAT;
  schemaVersion: typeof LMS_QUESTIONS_SCHEMA_VERSION;
  questions: LmsQuestion[];
  skipped: LmsQuestionsSkipped[];
}

export interface LmsQuestionsParseResult {
  questions: LmsQuestion[];
  skipped: LmsQuestionsSkipped[];
  errors: string[];
}

export class LmsQuestionsFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LmsQuestionsFileError';
  }
}

export function isQuestionType(value: unknown): value is QuestionType {
  return typeof value === 'string' && (QUESTION_TYPES as readonly string[]).includes(value);
}

export function questionHasStoredOptions(type: QuestionType): boolean {
  return (
    type === 'single_choice' ||
    type === 'multiple_choice' ||
    type === 'true_false' ||
    type === 'short_answer' ||
    type === 'long_answer'
  );
}

function unknownKeys(value: Record<string, unknown>, allowed: Set<string>): string[] {
  return Object.keys(value).filter((key) => !allowed.has(key));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nullableString(value: unknown, path: string): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'string') {
    throw new Error(`${path} debe ser texto o null.`);
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeOptions(options: unknown, path: string, strictKeys: boolean): LmsQuestionOption[] {
  if (options === undefined || options === null) return [];
  if (!Array.isArray(options)) {
    throw new Error(`${path} debe ser un arreglo.`);
  }

  return options.map((raw, index) => {
    const optionPath = `${path}[${index}]`;
    if (!isPlainObject(raw)) {
      throw new Error(`${optionPath} debe ser un objeto.`);
    }
    if (strictKeys) {
      const extra = unknownKeys(raw, OPTION_KEYS);
      if (extra.length > 0) {
        throw new Error(`${optionPath} contiene campos no permitidos: ${extra.join(', ')}.`);
      }
    }
    if (typeof raw.content !== 'string' || !raw.content.trim()) {
      throw new Error(`${optionPath}.content es obligatorio.`);
    }
    const content = raw.content.trim();
    if (content.length > OPTION_CONTENT_MAX_LENGTH) {
      throw new Error(`${optionPath}.content no puede superar ${OPTION_CONTENT_MAX_LENGTH} caracteres.`);
    }
    if (typeof raw.isCorrect !== 'boolean') {
      throw new Error(`${optionPath}.isCorrect debe ser boolean.`);
    }
    if (raw.label !== undefined && raw.label !== null && typeof raw.label !== 'string') {
      throw new Error(`${optionPath}.label debe ser texto.`);
    }
    const label = typeof raw.label === 'string' && raw.label.trim() ? raw.label.trim() : undefined;
    return { content, isCorrect: raw.isCorrect, ...(label ? { label } : {}) };
  });
}

export function validateQuestionSemantics(input: {
  title: unknown;
  type: unknown;
  stem: unknown;
  options?: unknown;
  requiresReview?: boolean;
}, path = ''): { title: string; type: QuestionType; stem: string; options: LmsQuestionOption[] } {
  const prefix = path ? `${path}.` : '';

  if (typeof input.title !== 'string' || !input.title.trim()) {
    throw new Error(`${prefix}title es obligatorio.`);
  }
  const title = input.title.trim();
  if (title.length > TITLE_MAX_LENGTH) {
    throw new Error(`${prefix}title no puede superar ${TITLE_MAX_LENGTH} caracteres.`);
  }

  if (!isQuestionType(input.type)) {
    throw new Error(
      `${prefix}type debe ser uno de: ${QUESTION_TYPES.join(', ')}.`,
    );
  }

  if (typeof input.stem !== 'string' || !input.stem.trim()) {
    throw new Error(`${prefix}stem es obligatorio.`);
  }
  const stem = input.stem.trim();
  if (stem.length > STEM_MAX_LENGTH) {
    throw new Error(`${prefix}stem no puede superar ${STEM_MAX_LENGTH} caracteres.`);
  }

  const options = normalizeOptions(input.options, `${prefix}options`, false);
  assertOptionsForType(input.type, options, `${prefix}options`, Boolean(input.requiresReview));
  return { title, type: input.type, stem, options };
}

export function assertOptionsForType(
  type: QuestionType,
  options: LmsQuestionOption[],
  path = 'options',
  requiresReview = false,
): void {
  const correctCount = options.filter((option) => option.isCorrect).length;

  if (type === 'file_upload') {
    if (options.length > 0) {
      throw new Error(`${path} debe estar vacío para ${type}.`);
    }
    return;
  }

  if (type === 'long_answer') {
    if (options.length === 0) return;
    if (correctCount !== options.length) {
      throw new Error(`${path}: todas las respuestas aceptadas deben tener isCorrect: true.`);
    }
    return;
  }

  if (type === 'single_choice') {
    if (options.length < 2) {
      throw new Error(`${path} requiere al menos 2 opciones.`);
    }
    if (correctCount !== 1 && !(requiresReview && correctCount === 0)) {
      throw new Error(`${path} debe tener exactamente 1 opción correcta.`);
    }
    return;
  }

  if (type === 'multiple_choice') {
    if (options.length < 2) {
      throw new Error(`${path} requiere al menos 2 opciones.`);
    }
    if (correctCount < 1 && !requiresReview) {
      throw new Error(`${path} debe tener al menos 1 opción correcta.`);
    }
    return;
  }

  if (type === 'true_false') {
    if (options.length !== 2) {
      throw new Error(`${path} debe tener exactamente 2 opciones: Verdadero y Falso.`);
    }
    const contents = options.map((option) => option.content);
    const hasTrue = contents.includes('Verdadero');
    const hasFalse = contents.includes('Falso');
    if (!hasTrue || !hasFalse) {
      throw new Error(`${path} debe contener exactamente "Verdadero" y "Falso".`);
    }
    if (correctCount !== 1 && !(requiresReview && correctCount === 0)) {
      throw new Error(`${path} debe tener exactamente 1 opción correcta.`);
    }
    return;
  }

  if (type === 'short_answer') {
    if (options.length < 1) {
      throw new Error(`${path} requiere al menos 1 respuesta aceptada.`);
    }
    if (correctCount !== options.length) {
      throw new Error(`${path}: todas las respuestas aceptadas deben tener isCorrect: true.`);
    }
    return;
  }
}

function parseRequiresReview(value: unknown, type: QuestionType, path: string): boolean {
  if (questionTypeRequiresReview(type)) return true;
  if (value === undefined) return false;
  if (typeof value !== 'boolean') {
    throw new Error(`${path}.requiresReview debe ser boolean.`);
  }
  return value;
}

function parseQuestion(raw: unknown, path: string): LmsQuestion {
  if (!isPlainObject(raw)) {
    throw new Error(`${path} debe ser un objeto.`);
  }
  const extra = unknownKeys(raw, QUESTION_KEYS);
  if (extra.length > 0) {
    throw new Error(`${path} contiene campos no permitidos: ${extra.join(', ')}.`);
  }

  const requiresReview = isQuestionType(raw.type)
    ? parseRequiresReview(raw.requiresReview, raw.type, path)
    : false;
  const base = validateQuestionSemantics(
    {
      title: raw.title,
      type: raw.type,
      stem: raw.stem,
      options: raw.options,
      requiresReview,
    },
    path,
  );
  const explanation = nullableString(raw.explanation, `${path}.explanation`);
  const feedbackCorrect = nullableString(raw.feedbackCorrect, `${path}.feedbackCorrect`);
  const feedbackIncorrect = nullableString(raw.feedbackIncorrect, `${path}.feedbackIncorrect`);
  const options = normalizeOptions(raw.options, `${path}.options`, true);
  assertOptionsForType(base.type, options, `${path}.options`, requiresReview);

  return {
    title: base.title,
    type: base.type,
    stem: base.stem,
    explanation,
    feedbackCorrect,
    feedbackIncorrect,
    requiresReview,
    ...(questionHasStoredOptions(base.type) ? { options } : {}),
  };
}

function parseSkipped(raw: unknown): LmsQuestionsSkipped[] {
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) {
    throw new LmsQuestionsFileError('skipped debe ser un arreglo.');
  }
  return raw.map((item, index) => {
    if (!isPlainObject(item)) {
      return { reason: `Entrada skipped[${index}] no válida.` };
    }
    const reason = typeof item.reason === 'string' && item.reason.trim() ? item.reason.trim() : 'Omitida.';
    const sourceExcerpt =
      typeof item.sourceExcerpt === 'string' && item.sourceExcerpt.trim()
        ? item.sourceExcerpt.trim()
        : undefined;
    return { reason, ...(sourceExcerpt ? { sourceExcerpt } : {}) };
  });
}

export function parseLmsQuestionsJson(content: string): LmsQuestionsParseResult {
  const normalized = content.replace(/^\uFEFF/, '').trim();
  if (!normalized) {
    throw new LmsQuestionsFileError('El archivo JSON está vacío.');
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(normalized);
  } catch {
    throw new LmsQuestionsFileError('El archivo no es un JSON válido.');
  }

  if (!isPlainObject(parsed)) {
    throw new LmsQuestionsFileError('El JSON debe ser un objeto lms-questions.');
  }

  const extra = unknownKeys(parsed, ENVELOPE_KEYS);
  if (extra.length > 0) {
    throw new LmsQuestionsFileError(`El archivo contiene campos no permitidos: ${extra.join(', ')}.`);
  }

  if (parsed.format !== LMS_QUESTIONS_FORMAT) {
    throw new LmsQuestionsFileError(
      `format debe ser "${LMS_QUESTIONS_FORMAT}".`,
    );
  }
  if (parsed.schemaVersion !== LMS_QUESTIONS_SCHEMA_VERSION) {
    throw new LmsQuestionsFileError(
      `schemaVersion debe ser ${LMS_QUESTIONS_SCHEMA_VERSION}.`,
    );
  }
  if (!Array.isArray(parsed.questions)) {
    throw new LmsQuestionsFileError('questions debe ser un arreglo.');
  }

  const skipped = parseSkipped(parsed.skipped);
  const questions: LmsQuestion[] = [];
  const errors: string[] = [];

  for (const [index, item] of parsed.questions.entries()) {
    const path = `questions[${index}]`;
    try {
      questions.push(parseQuestion(item, path));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Pregunta inválida.';
      errors.push(message);
    }
  }

  return { questions, skipped, errors };
}

export const LMS_QUESTIONS_EXAMPLE: LmsQuestionsFile = {
  format: LMS_QUESTIONS_FORMAT,
  schemaVersion: LMS_QUESTIONS_SCHEMA_VERSION,
  questions: [
    {
      title: 'Capital de Francia',
      type: 'single_choice',
      stem: '¿Cuál es la capital de Francia?',
      explanation: 'París es la capital de Francia.',
      feedbackCorrect: null,
      feedbackIncorrect: null,
      requiresReview: false,
      options: [
        { label: 'A', content: 'París', isCorrect: true },
        { label: 'B', content: 'Londres', isCorrect: false },
        { label: 'C', content: 'Madrid', isCorrect: false },
      ],
    },
    {
      title: 'Protocolo PAS',
      type: 'multiple_choice',
      stem: 'Seleccione las acciones del protocolo PAS.',
      explanation: null,
      feedbackCorrect: null,
      feedbackIncorrect: null,
      requiresReview: false,
      options: [
        { label: 'A', content: 'Proteger', isCorrect: true },
        { label: 'B', content: 'Alertar', isCorrect: true },
        { label: 'C', content: 'Socorrer', isCorrect: true },
        { label: 'D', content: 'Acelerar', isCorrect: false },
      ],
    },
    {
      title: 'Cielo verde',
      type: 'true_false',
      stem: 'El cielo es verde.',
      explanation: null,
      feedbackCorrect: null,
      feedbackIncorrect: null,
      requiresReview: false,
      options: [
        { label: 'V', content: 'Verdadero', isCorrect: false },
        { label: 'F', content: 'Falso', isCorrect: true },
      ],
    },
    {
      title: 'Abreviatura OMS',
      type: 'short_answer',
      stem: '¿Qué significan las siglas OMS?',
      explanation: null,
      feedbackCorrect: null,
      feedbackIncorrect: null,
      requiresReview: false,
      options: [{ content: 'Organización Mundial de la Salud', isCorrect: true }],
    },
    {
      title: 'Ensayo de seguridad',
      type: 'long_answer',
      stem: 'Explique por qué es importante usar EPP en obra.',
      explanation: null,
      feedbackCorrect: null,
      feedbackIncorrect: null,
      requiresReview: true,
      options: [{ content: 'usar EPP', isCorrect: true }],
    },
    {
      title: 'Constancia firmada',
      type: 'file_upload',
      stem: 'Suba el documento de constancia firmado.',
      explanation: null,
      feedbackCorrect: null,
      feedbackIncorrect: null,
      requiresReview: true,
    },
  ],
  skipped: [],
};

export const LMS_QUESTIONS_AI_PROMPT = `Eres un conversor de bancos de preguntas. Devuelve ÚNICAMENTE un JSON válido, sin markdown ni comentarios, con este sobre:

{
  "format": "lms-questions",
  "schemaVersion": 1,
  "questions": [],
  "skipped": []
}

Tipos permitidos (y ningún otro): single_choice, multiple_choice, true_false, short_answer, long_answer, file_upload.

Reglas por tipo:
- single_choice: mínimo 2 opciones y exactamente 1 con isCorrect true. Si requiresReview es true, puede no haber ninguna correcta; no inventes una
- multiple_choice: mínimo 2 opciones y al menos 1 con isCorrect true. Si requiresReview es true, puede no haber ninguna correcta
- true_false: exactamente 2 opciones con content "Verdadero" y "Falso" y exactamente 1 correcta. Si requiresReview es true, puede no haber ninguna correcta
- short_answer: 1 o más respuestas aceptadas; todas con isCorrect true
- long_answer: opcionalmente 1 o más respuestas aceptadas, todas con isCorrect true. Siempre queda en revisión humana; requiresReview false se ignora
- file_upload: sin options (omítelas o usa []). Siempre queda en revisión humana; requiresReview false se ignora
- requiresReview: boolean. true deja la pregunta para revisión humana y no se autocorrige. false se autocorrige. Si se omite, queda en false, salvo long_answer y file_upload, que siempre son true

Cada pregunta solo puede tener: title, type, stem, explanation, feedbackCorrect, feedbackIncorrect, options, requiresReview.
Cada opción solo puede tener: label, content, isCorrect.
No inventes id ni campos extra. explanation/feedbackCorrect/feedbackIncorrect son string o null.
Si una pregunta de origen no se puede mapear a estos tipos, no la conviertas: agrégala a skipped como { "reason": "...", "sourceExcerpt": "..." }.`;
